import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { WorkerStatus } from '../types';

// ═══════════════════════════════════════════════════════════
//  Worker3D — "wow" CSS-3D robot avatar
//  Layered preserve-3d + framer-motion for depth, materials,
//  LED eyes, chest reactor, antenna, orbiting particles and
//  a holographic base disk that reacts to status.
// ═══════════════════════════════════════════════════════════

interface WorkerColors {
  body: string;       // primary torso / limbs
  accent: string;     // highlights, reactor core
  rim: string;        // rim-light color
  eye: string;        // LED eye color
}

const workerColors: Record<string, WorkerColors> = {
  'claude-coder':         { body: '#8b5cf6', accent: '#c4b5fd', rim: '#ec4899', eye: '#fef3c7' },
  // Sage/teal for Sonny so the cheap variant reads as visually
  // distinct from Claude's purple — same role, different cost class.
  'claude-coder-sonnet':  { body: '#3f9272', accent: '#7dd3a8', rim: '#86efac', eye: '#ecfdf5' },
  'claw-orchestrator':    { body: '#f97316', accent: '#fdba74', rim: '#fde047', eye: '#fef3c7' },
  'claude-tester':        { body: '#06b6d4', accent: '#67e8f9', rim: '#22d3ee', eye: '#f0f9ff' },
  'miro-architect':       { body: '#10b981', accent: '#6ee7b7', rim: '#a7f3d0', eye: '#ecfdf5' },
};

const statusAccent: Record<WorkerStatus, string> = {
  idle:    '#475569',
  working: '#22c55e',
  done:    '#3b82f6',
  error:   '#ef4444',
  waiting: '#eab308',
};

interface Props {
  workerId: string;
  status: WorkerStatus;
}

export const Worker3D: React.FC<Props> = ({ workerId, status }) => {
  const colors = workerColors[workerId] || workerColors['claude-coder'];
  const isWorking = status === 'working';
  const isDone = status === 'done';
  const isError = status === 'error';
  const isWaiting = status === 'waiting';
  const glow = statusAccent[status];

  // Stable orbit offsets so re-renders don't jitter particles
  const particles = useMemo(
    () => Array.from({ length: 6 }, (_, i) => ({
      delay: i * 0.35,
      startAngle: (i * 60),
      scale: 0.6 + (i % 3) * 0.18,
    })),
    []
  );

  // Float / sway / shake based on status
  const bodyAnim = isWorking
    ? { rotateY: [0, 6, -6, 0], y: [0, -2, 0] }
    : isDone
    ? { y: [0, -10, 0], scale: [1, 1.04, 1], rotateY: [0, 360, 360] }
    : isError
    ? { rotateZ: [0, -4, 4, 0], x: [0, -1, 1, 0] }
    : { y: [0, -3, 0], rotateY: [-3, 3, -3] };

  return (
    <div className="worker3d-stage">
      {/* Ambient back glow */}
      <div
        className="worker3d-back-glow"
        style={{ background: `radial-gradient(circle, ${glow}33 0%, transparent 65%)` }}
      />

      <motion.div
        className="worker3d-root"
        animate={bodyAnim}
        transition={{
          duration: isWorking ? 1.4 : isDone ? 3 : 3.2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        {/* ═══ Antenna ═══ */}
        <div className="w3d-antenna">
          <div className="w3d-antenna-rod" style={{ background: `linear-gradient(180deg, ${colors.accent}, ${colors.body})` }} />
          <motion.div
            className="w3d-antenna-tip"
            style={{ background: glow, boxShadow: `0 0 14px ${glow}, 0 0 28px ${glow}99` }}
            animate={{ scale: [1, 1.5, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: isWorking ? 0.8 : 2, repeat: Infinity }}
          />
        </div>

        {/* ═══ Head ═══ */}
        <motion.div
          className="w3d-head"
          animate={
            isWorking ? { rotateY: [-10, 10, -10], rotateX: [-4, 4, -4] } :
            isError ? { rotateZ: [-6, 6, -6] } :
            { rotateY: [-6, 6, -6] }
          }
          transition={{ duration: isWorking ? 1 : 3.2, repeat: Infinity, ease: 'easeInOut' }}
        >
          {/* Helmet shell with specular highlight */}
          <div
            className="w3d-head-shell"
            style={{
              background: `linear-gradient(145deg, ${colors.accent} 0%, ${colors.body} 55%, #0f172a 100%)`,
              boxShadow: `inset -6px -8px 14px rgba(0,0,0,0.4), inset 4px 4px 10px ${colors.accent}99, 4px 6px 18px rgba(0,0,0,0.6)`,
            }}
          >
            {/* Visor */}
            <div
              className="w3d-visor"
              style={{
                background: `linear-gradient(180deg, #0f172a 0%, ${colors.body}22 60%, ${colors.accent}cc 100%)`,
                boxShadow: `inset 0 2px 4px rgba(255,255,255,0.15), 0 0 12px ${glow}55`,
              }}
            >
              {/* LED eyes */}
              <motion.div
                className="w3d-eye w3d-eye-l"
                style={{ background: colors.eye, boxShadow: `0 0 6px ${colors.eye}, 0 0 12px ${glow}` }}
                animate={isWorking ? { opacity: [1, 1, 0.1, 1] } : { opacity: [1, 0.6, 1] }}
                transition={{ duration: isWorking ? 2.8 : 3.5, repeat: Infinity, repeatDelay: 0.2 }}
              />
              <motion.div
                className="w3d-eye w3d-eye-r"
                style={{ background: colors.eye, boxShadow: `0 0 6px ${colors.eye}, 0 0 12px ${glow}` }}
                animate={isWorking ? { opacity: [1, 1, 0.1, 1] } : { opacity: [1, 0.6, 1] }}
                transition={{ duration: isWorking ? 2.8 : 3.5, repeat: Infinity, repeatDelay: 0.2 }}
              />
              {/* Visor scanline */}
              <motion.div
                className="w3d-scanline"
                animate={{ y: [-4, 14, -4] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: 'linear' }}
              />
            </div>
            {/* Ear-plates */}
            <div className="w3d-ear w3d-ear-l" style={{ background: colors.body }} />
            <div className="w3d-ear w3d-ear-r" style={{ background: colors.body }} />
          </div>
        </motion.div>

        {/* ═══ Neck ═══ */}
        <div className="w3d-neck" style={{ background: `linear-gradient(180deg, ${colors.accent}, #1f2937)` }} />

        {/* ═══ Torso ═══ */}
        <div
          className="w3d-torso"
          style={{
            background: `linear-gradient(145deg, ${colors.accent} 0%, ${colors.body} 50%, #0f172a 100%)`,
            boxShadow: `inset -5px -8px 14px rgba(0,0,0,0.45), inset 4px 4px 10px ${colors.accent}aa, 4px 8px 20px rgba(0,0,0,0.5)`,
          }}
        >
          {/* Reactor core */}
          <motion.div
            className="w3d-reactor"
            style={{
              background: `radial-gradient(circle, ${colors.eye} 0%, ${glow} 35%, transparent 75%)`,
              boxShadow: `0 0 18px ${glow}, 0 0 36px ${glow}99, inset 0 0 8px ${colors.eye}`,
            }}
            animate={{ scale: [1, 1.18, 1], opacity: [0.85, 1, 0.85] }}
            transition={{ duration: isWorking ? 0.9 : 2.4, repeat: Infinity, ease: 'easeInOut' }}
          />
          {/* Chest seam lines */}
          <div className="w3d-seam w3d-seam-v" />
          <div className="w3d-seam w3d-seam-h" />
          {/* Shoulder pads */}
          <div className="w3d-shoulder w3d-shoulder-l" style={{ background: colors.body }} />
          <div className="w3d-shoulder w3d-shoulder-r" style={{ background: colors.body }} />
        </div>

        {/* ═══ Arms ═══ */}
        <motion.div
          className="w3d-arm w3d-arm-l"
          animate={
            isWorking ? { rotateX: [-55, -70, -55], rotateZ: [6, 2, 6] } :
            isDone ? { rotateZ: [15, 80, 15], rotateX: [-100, -140, -100] } :
            isError ? { rotateX: [-20, -10, -20], rotateZ: [15, 25, 15] } :
            { rotateZ: [8, 12, 8], rotateX: [-5, 0, -5] }
          }
          transition={{ duration: isWorking ? 0.45 : 2.2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="w3d-upper-arm" style={{ background: `linear-gradient(145deg, ${colors.accent}, ${colors.body})` }} />
          <div className="w3d-forearm" style={{ background: `linear-gradient(145deg, ${colors.body}, #0f172a)` }} />
          <div className="w3d-hand" style={{ background: colors.accent }} />
        </motion.div>
        <motion.div
          className="w3d-arm w3d-arm-r"
          animate={
            isWorking ? { rotateX: [-70, -55, -70], rotateZ: [-6, -2, -6] } :
            isDone ? { rotateZ: [-15, -80, -15], rotateX: [-100, -140, -100] } :
            isError ? { rotateX: [-90, -100, -90], rotateZ: [-10, 0, -10] } :
            { rotateZ: [-8, -12, -8], rotateX: [-5, 0, -5] }
          }
          transition={{ duration: isWorking ? 0.45 : 2.2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="w3d-upper-arm" style={{ background: `linear-gradient(145deg, ${colors.accent}, ${colors.body})` }} />
          <div className="w3d-forearm" style={{ background: `linear-gradient(145deg, ${colors.body}, #0f172a)` }} />
          <div className="w3d-hand" style={{ background: colors.accent }} />
        </motion.div>

        {/* ═══ Hover pad (no legs — floating robot) ═══ */}
        <div className="w3d-hover-pad" style={{ background: `linear-gradient(180deg, ${colors.body}, #0f172a)` }} />

        {/* ═══ Holographic working panel ═══ */}
        {isWorking && (
          <motion.div
            className="w3d-holo"
            initial={{ opacity: 0, rotateX: -30 }}
            animate={{ opacity: [0.7, 1, 0.7], rotateX: [-30, -25, -30] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              background: `linear-gradient(180deg, ${colors.eye}22, ${glow}55)`,
              border: `1px solid ${glow}`,
              boxShadow: `0 0 18px ${glow}, inset 0 0 12px ${glow}66`,
            }}
          >
            <motion.div
              className="w3d-holo-line"
              animate={{ width: ['20%', '90%', '35%', '75%'] }}
              transition={{ duration: 1.6, repeat: Infinity }}
              style={{ background: colors.eye }}
            />
            <motion.div
              className="w3d-holo-line"
              animate={{ width: ['60%', '30%', '85%', '45%'] }}
              transition={{ duration: 1.9, repeat: Infinity }}
              style={{ background: colors.accent }}
            />
            <motion.div
              className="w3d-holo-line"
              animate={{ width: ['40%', '70%', '25%', '65%'] }}
              transition={{ duration: 1.4, repeat: Infinity }}
              style={{ background: glow }}
            />
          </motion.div>
        )}

        {/* ═══ Status hovering icon ═══ */}
        {isDone && (
          <motion.div
            className="w3d-badge"
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: [0, 1.2, 1], rotate: [0, 10, 0] }}
            transition={{ duration: 0.6 }}
            style={{ background: '#3b82f6', boxShadow: '0 0 16px #3b82f6' }}
          >
            ✓
          </motion.div>
        )}
        {isError && (
          <motion.div
            className="w3d-badge"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 0.7, repeat: Infinity }}
            style={{ background: '#ef4444', boxShadow: '0 0 16px #ef4444' }}
          >
            !
          </motion.div>
        )}
        {isWaiting && (
          <motion.div
            className="w3d-badge"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            style={{ background: '#eab308', boxShadow: '0 0 14px #eab308' }}
          >
            ⏳
          </motion.div>
        )}
      </motion.div>

      {/* ═══ Orbiting particles ═══ */}
      {isWorking && particles.map((p, i) => (
        <motion.div
          key={i}
          className="w3d-particle"
          style={{ background: glow, boxShadow: `0 0 10px ${glow}` }}
          animate={{
            rotate: [p.startAngle, p.startAngle + 360],
            scale: [p.scale, p.scale * 1.3, p.scale],
          }}
          transition={{
            rotate: { duration: 4 + i * 0.3, repeat: Infinity, ease: 'linear', delay: p.delay },
            scale: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' },
          }}
        />
      ))}

      {/* ═══ Holographic base disk ═══ */}
      <div className="w3d-base-wrap">
        <motion.div
          className="w3d-base-disk"
          style={{
            background: `radial-gradient(ellipse, ${glow}88 0%, ${glow}33 40%, transparent 75%)`,
            boxShadow: `0 0 30px ${glow}aa, 0 0 60px ${glow}44`,
          }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.6, 0.9, 0.6] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="w3d-base-ring"
          style={{ borderColor: glow, boxShadow: `0 0 12px ${glow}` }}
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    </div>
  );
};
